import { SearchInput } from "@marketplace/shared/ui";
import { Button } from "@marketplace/ui";
import {
  DatePickerButton,
  type DatesRangeValue,
  type DateValue
} from "@marketplace/ui/dates";
import { getRouteApi } from "@tanstack/react-router";
import { format } from "date-fns";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDateRange, MdUpload } from "react-icons/md";

import { usePermissions } from "@/hooks";

import { exportOrderModalAtom } from "../../atoms/export-order-modal-atom";

const route = getRouteApi("/orders/");

export const OrderListFilter = () => {
  const { q, start_date, end_date } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("order");
  const setExportModal = useSetAtom(exportOrderModalAtom);
  const [search, setSearch] = useState(q ?? "");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null
  ]);
  const { isSuperAdmin } = usePermissions();

  useEffect(() => {
    const timeout = setTimeout(() => {
      navigate({
        search: (prev) => {
          const shouldResetPage = prev.q !== search;
          return {
            ...prev,
            q: search,
            ...(shouldResetPage && { page: 1 })
          };
        }
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [search, navigate]);

  // Sync start_date and end_date from search params to dateRange state
  useEffect(() => {
    const from = start_date ? new Date(start_date) : null;
    const to = end_date ? new Date(end_date) : null;
    const fromValid = from instanceof Date && !isNaN(from.getTime());
    const toValid = to instanceof Date && !isNaN(to.getTime());
    if (fromValid && toValid) {
      setDateRange([from, to]);
    } else {
      setDateRange([null, null]);
    }
  }, [start_date, end_date]);

  useEffect(() => {
    if (dateRange[0] === null && dateRange[1] === null) {
      navigate({
        search: (prev) => {
          const shouldResetPage =
            prev.start_date !== "" || prev.end_date !== "";
          return {
            ...prev,
            start_date: "",
            end_date: "",
            ...(shouldResetPage && { page: 1 })
          };
        }
      });
      return;
    }
  }, [dateRange, navigate]);

  const handleDateChange = (value: DateValue | DatesRangeValue | null) => {
    let range: [Date | null, Date | null] = [null, null];

    if (Array.isArray(value)) {
      range = [
        value[0] instanceof Date ? value[0] : null,
        value[1] instanceof Date ? value[1] : null
      ];
    } else if (
      value &&
      typeof value === "object" &&
      "from" in value &&
      "to" in value
    ) {
      range = [
        value.from instanceof Date ? value.from : null,
        value.to instanceof Date ? value.to : null
      ];
    }

    setDateRange(range);

    // Only navigate if both dates are selected
    if (range[0] && range[1]) {
      const startDate = format(range[0], "yyyy-MM-dd");
      const endDate = format(range[1], "yyyy-MM-dd");
      navigate({
        search: (prev) => ({
          ...prev,
          start_date: startDate,
          end_date: endDate,
          page: 1
        })
      });
    }
  };

  const handleOrderSearchChange = (value: string) => {
    setSearch(value);
  };

  return (
    <div className="flex justify-between items-center mb-6">
      <div>
        <SearchInput
          className="lg:w-[634px]"
          placeholder={t("orderListPage.searchPlaceholder")}
          onChange={handleOrderSearchChange}
          value={search}
        />
      </div>
      <div className="flex gap-x-[14px]">
        {}{" "}
        <DatePickerButton
          type="range"
          buttonProps={{
            rightSection: <MdDateRange />
          }}
          locale="id"
          format="DD MMM YYYY"
          label={t("orderListPage.date")}
          value={dateRange}
          onChange={handleDateChange}
          clearable
        />
        {isSuperAdmin ? (
          <Button
            variant="primary"
            rightSection={<MdUpload size={16} />}
            onClick={() => setExportModal({ visible: true })}
          >
            Export
          </Button>
        ) : null}
      </div>
    </div>
  );
};
